import * as React from 'react'
import { useDomEvent } from '@repo/hooks-use-dom-event'
import { getCookie, setCookie } from 'utils/dom'

const COOKIE_CHANGE_EVENT = 'cf_cookie_value_changed'

interface CookieChangeDetail {
  name: string
  value: string
}

function readCookie(name: string | undefined) {
  return name ? getCookie(name) || undefined : undefined
}

type SetCookieValue = (value: string) => void

export function useCookieValue(
  name: string | undefined,
): [value: string | undefined, set: SetCookieValue] {
  const [value, setValue] = React.useState(() => readCookie(name))

  useDomEvent(
    useDomEvent.document,
    COOKIE_CHANGE_EVENT,
    React.useCallback(
      (event: Event) => {
        const detail = (event as CustomEvent<CookieChangeDetail>).detail
        if (detail && detail.name === name) {
          setValue(detail.value || undefined)
        } else {
          setValue(readCookie(name))
        }
      },
      [name],
    ),
  )

  React.useEffect(() => {
    setValue(readCookie(name))
  }, [name])

  const set = React.useCallback<SetCookieValue>(
    newValue => {
      if (name) {
        setCookie({ name, value: newValue })
        document.dispatchEvent(
          new CustomEvent<CookieChangeDetail>(COOKIE_CHANGE_EVENT, {
            detail: { name, value: newValue },
          }),
        )
      }
    },
    [name],
  )

  return [value, set]
}
